import { useEffect, useMemo } from 'react';
import { shouldIgnoreKeyboardShortcut } from '@shared/helpers/event.helpers';
import { expressionPanel } from '@features/expressions/ExpressionInputPanel/ExpressionInputPanel';
import { hasNonEmptyTextSelection } from '@client/helpers/dom.helpers';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useSelectedVersion } from '@client/store/ui';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  groupImportedElementAdds,
  IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE,
  placeRemappedImportAdds,
} from '@shared/helpers/import-replace.helpers';
import { selectedProposalIdForVersion } from './import-replace-target.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import type { CandidateRow } from '@shared/models/element-import.model';
import type { ProjectElementImport } from '@shared/models/element-import.model';
import {
  buildBulkMappingChanges,
  bulkUpdatedMessage,
} from './bulk-row-actions.helpers';
import type { ImportedElementsByRowId } from './candidate-status.helpers';
import type { MappingsByCode } from './element-imports.store';
import { resolveImportCandidateRowIdForDiscard } from './import-list-navigation.helpers';
import {
  collectSelectedCandidateRows,
  findCandidateRowById,
  resolveCandidateRowMappingReference,
} from './import-mapping-clipboard.helpers';
import {
  getCopiedImportMapping,
  setCopiedImportMapping,
} from './import-mapping-clipboard.store';
import {
  getVisibleListRowIds,
  getVisibleListRowIdsSnapshot,
} from './list-selection.store';

type UseImportMappingClipboardHotkeysArgs = {
  selectedRowId: string | null;
  multiSelectedIds: ReadonlySet<string>;
  rowsByImport: Record<string, CandidateRow[]>;
  mappingsByCode: MappingsByCode;
  importsById: Record<string, ProjectElementImport | undefined>;
  visibleImports: ProjectElementImport[];
  importedElements: ImportedElementsByRowId;
};

/**
 * Cmd/Ctrl+C copies the active import row's product/recipe mapping; Cmd/Ctrl+V
 * pastes it onto the multi-selection via {@link buildBulkMappingChanges}.
 * A non-empty text selection keeps native copy (e.g. Type "TR03-…") so the
 * mapping shortcut does not steal the system clipboard.
 */
export const useImportMappingClipboardHotkeys = ({
  selectedRowId,
  multiSelectedIds,
  rowsByImport,
  mappingsByCode,
  importsById,
  visibleImports,
  importedElements,
}: UseImportMappingClipboardHotkeysArgs): void => {
  const version = useSelectedVersion();
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const applyChanges = useApplyProjectChanges();
  const { enqueueSnackbar } = useSnackbar();
  const isReadonly = useIsReadonly();

  const lookups = useMemo(() => {
    if (!version) return undefined;
    return {
      version,
      mappingsByCode,
      productsById,
      recipesById,
      importsById,
      importedElements,
    };
  }, [
    version,
    mappingsByCode,
    productsById,
    recipesById,
    importsById,
    importedElements,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreKeyboardShortcut(event) || expressionPanel.isOpen()) {
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key !== 'c' && key !== 'v') return;

      if (key === 'c') {
        // Highlighted Type/name text wins over mapping copy.
        if (hasNonEmptyTextSelection()) return;

        const rowId = resolveImportCandidateRowIdForDiscard(
          selectedRowId ?? undefined,
          visibleImports,
          rowsByImport,
          importedElements,
        );
        if (!rowId) return;
        const { ids, reported } = getVisibleListRowIdsSnapshot();
        if (reported && !ids.has(rowId)) return;
        const row = findCandidateRowById(rowsByImport, rowId);
        if (!row) return;
        const mapping = resolveCandidateRowMappingReference(row, {
          version,
          mappingsByCode,
          importedElements,
        });
        if (!mapping) {
          enqueueSnackbar('No climate data mapping to copy', {
            variant: 'info',
            autoHideDuration: 2000,
            preventDuplicate: true,
          });
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        setCopiedImportMapping(mapping);
        enqueueSnackbar('Copied mapping', {
          variant: 'info',
          autoHideDuration: 2000,
          preventDuplicate: true,
        });
        return;
      }

      // Paste
      if (isReadonly || !lookups) return;
      const copied = getCopiedImportMapping();
      if (!copied) return;

      const selectedRows = collectSelectedCandidateRows(
        rowsByImport,
        multiSelectedIds,
        getVisibleListRowIds(),
      );
      if (selectedRows.length === 0) return;

      event.preventDefault();
      event.stopPropagation();
      const result = buildBulkMappingChanges(
        selectedRows,
        lookups,
        copied.referenceId,
        copied.referenceType,
      );
      if (result.appliedRowIds.length === 0 || result.changes.length === 0) {
        return;
      }
      const proposalId = selectedProposalIdForVersion(lookups.version);
      const grouped = groupImportedElementAdds(
        lookups.version,
        proposalId,
        result.changes,
      );
      if (grouped.needsProposal) {
        enqueueSnackbar(IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE, {
          variant: 'warning',
        });
        return;
      }
      const placed = placeRemappedImportAdds(
        lookups.version,
        proposalId,
        grouped.changes,
      );
      if (placed.length === 0) return;
      void applyChanges(placed, {
        snackbar: {
          type: 'undo',
          message: bulkUpdatedMessage(result.appliedRowIds),
        },
      });
    };

    window.document.addEventListener('keydown', onKeyDown);
    return () => window.document.removeEventListener('keydown', onKeyDown);
  }, [
    selectedRowId,
    multiSelectedIds,
    rowsByImport,
    mappingsByCode,
    visibleImports,
    importedElements,
    version,
    lookups,
    applyChanges,
    enqueueSnackbar,
    isReadonly,
  ]);
};
